import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

let cachedSeoData = null;

export const usePageSEO = (pageKey, customData = {}) => {
    const { i18n } = useTranslation();
    const location = useLocation();
    const [seoData, setSeoData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const currentLang = i18n.language;
    const siteUrl = process.env.REACT_APP_SITE_URL || 'https://carabinex.com';

    useEffect(() => {
        const fetchSeoData = async () => {
            try {
                setLoading(true);

                if (cachedSeoData) {
                    setSeoData(cachedSeoData);
                    setLoading(false);
                    return;
                }

                const response = await fetch('https://carabinex.onrender.com/api/content/seo');

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();
                cachedSeoData = data;
                setSeoData(data);
            } catch (err) {
                console.error('❌ Failed to load seo.json:', err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchSeoData();
    }, []);

    if (loading || error || !seoData) {
        return {
            title: 'Carabinex',
            description: 'Strategy, creativity, analytics, SEO, PPC, SMM, web design and development.',
            keywords: 'SEO, PPC, SMM, web design',
            url: `${siteUrl}${location.pathname}`,
            type: 'website',
            image: `${siteUrl}/images/og-default.jpg`,
            robots: 'index, follow',
            noIndex: false,
            noFollow: false,
            loading: true,
        };
    }

    const pageData = seoData.pages?.[pageKey];

    if (!pageData) {
        console.warn(`⚠️ SEO data not found for page: ${pageKey}`);
        return {
            title: 'Carabinex',
            description: 'Strategy, creativity, analytics, SEO, PPC, SMM, web design and development.',
            url: `${siteUrl}${location.pathname}`,
            type: 'website',
            robots: 'index, follow',
            noIndex: false,
            noFollow: false,
        };
    }

    const title = pageData.title?.[currentLang] || pageData.title?.en || 'Carabinex';
    const description = pageData.description?.[currentLang] || pageData.description?.en || '';
    const keywords = pageData.keywords?.[currentLang] || pageData.keywords?.en || '';
    const type = pageData.type || 'website';
    const robots = pageData.robots || 'index, follow';

    const robotsParts = robots.split(', ');
    const noIndex = robotsParts.includes('noindex');
    const noFollow = robotsParts.includes('nofollow');

    const langPrefix = currentLang === 'en' ? '' : `/${currentLang}`;
    const fullUrl = customData.url || `${siteUrl}${langPrefix}${location.pathname}`;

    const defaultImage = 'https://carabinex.onrender.com/images/og-default.jpg';
    const image = customData.image || pageData.image || defaultImage;

    return {
        title: customData.title || title,
        description: customData.description || description,
        keywords: customData.keywords || keywords,
        url: fullUrl,
        type: customData.type || type,
        image,
        robots,
        noIndex,
        noFollow,
        loading: false,
    };
};